import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { apiFetch } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

interface EmployeeDto {
  id: number; firstName: string; lastName: string; fullName: string;
  email: string | null; jobTitle: string | null; deleted: boolean;
  roles: string[]; identityLockedOut: boolean;
}
interface Paged<T> { items: T[]; page: number; pageSize: number; totalCount: number; }

export function EmployeesListPage() {
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["employees", page, q, includeDeleted],
    queryFn: () => apiFetch<Paged<EmployeeDto>>(
      `/api/employees?page=${page}&pageSize=50&q=${encodeURIComponent(q)}${includeDeleted ? "" : "&deleted=false"}`),
  });

  const createMut = useMutation({
    mutationFn: (req: { firstName: string; lastName: string; email: string; roles: string[] }) =>
      apiFetch<EmployeeDto>("/api/employees", { method: "POST", body: JSON.stringify(req) }),
    onSuccess: (created) => { setDrawerOpen(false); void qc.invalidateQueries({ queryKey: ["employees"] }); void nav(`/admin/employees/${created.id}`); },
  });

  const cols: DataGridColumn<EmployeeDto>[] = [
    { id: "name",  header: "Name",   accessor: (r) => r.fullName, sortable: true },
    { id: "email", header: "Email",  accessor: (r) => r.email ?? "—" },
    { id: "title", header: "Title",  accessor: (r) => r.jobTitle ?? "—" },
    { id: "roles", header: "Roles",  accessor: (r) => r.roles.join(", ") || "—" },
    { id: "state", header: "State",  accessor: (r) => r.deleted ? "(deleted)" : r.identityLockedOut ? "Locked" : "Active" },
  ];

  return (
    <ListPage
      title="Employees"
      primaryAction={<IntentButton onClick={() => setDrawerOpen(true)}>New employee</IntentButton>}
      filterBar={
        <div className="flex items-end gap-3">
          <div className="grow">
            <Label htmlFor="q">Search</Label>
            <Input id="q" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Name or email" />
          </div>
          <div className="flex items-center gap-2"><Switch id="d" checked={includeDeleted} onCheckedChange={setIncludeDeleted} /><Label htmlFor="d">Show deleted</Label></div>
        </div>
      }>
      <DataGrid
        rows={data?.items ?? []} columns={cols} rowKey={(r) => r.id}
        totalRows={data?.totalCount ?? 0} pageIndex={page} pageSize={50}
        loading={isLoading} onPageChange={setPage}
      />
      <DetailsDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title="New employee">
        <NewEmployeeForm onSubmit={(req) => createMut.mutate(req)} pending={createMut.isPending} />
      </DetailsDrawer>
    </ListPage>
  );
}

function NewEmployeeForm({ onSubmit, pending }: { onSubmit: (r: { firstName: string; lastName: string; email: string; roles: string[] }) => void; pending: boolean }) {
  const [firstName, setF] = useState(""); const [lastName, setL] = useState(""); const [email, setE] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ firstName, lastName, email, roles: ["ReadOnly"] }); }} className="space-y-3">
      <div><Label htmlFor="fn">First name</Label><Input id="fn" value={firstName} onChange={(e) => setF(e.target.value)} required /></div>
      <div><Label htmlFor="ln">Last name</Label><Input id="ln" value={lastName} onChange={(e) => setL(e.target.value)} required /></div>
      <div><Label htmlFor="em">Email</Label><Input id="em" type="email" value={email} onChange={(e) => setE(e.target.value)} required /></div>
      <Button type="submit" disabled={pending}>{pending ? "Creating…" : "Create + send welcome"}</Button>
    </form>
  );
}
